// Deterministic sizing/bitrate policy for native (WebCodecs) upload compression.
//
// WebCodecs has no notion of x264 CRF, so this module never pretends to have one: it derives
// an explicit target video bitrate from the desired output file size and the video's duration,
// bounded by a fixed quality ceiling so short videos are not needlessly bloated just because
// project.maxVideoSizeMB happens to be large.

export const NATIVE_RESOLUTIONS = {
    720: { width: 1280, height: 720 },
    480: { width: 854, height: 480 },
} as const

export type NativeTargetResolution = keyof typeof NATIVE_RESOLUTIONS

// Reserve part of the size budget for MP4 container/muxing overhead and encoder bitrate
// variance (WebCodecs' `bitrate` option is a target, not an exact byte count).
export const CONTAINER_SAFETY_MARGIN = 0.85

// Never exceed this bitrate even when maxVideoSizeMB would allow it, matching the bitrate
// already used by the existing WebCodecs download exporter for 720p (see WebCodecsExporter.ts).
export const BITRATE_CEILING_BPS: Record<NativeTargetResolution, number> = {
    720: 2_500_000,
    480: 1_200_000,
}

// Never encode below this, even for a very long source, so the result stays watchable.
export const BITRATE_FLOOR_BPS = 300_000

export interface BitrateBudget {
    bitrateBps: number
    usableBytes: number
}

// Computes a bounded target video bitrate for a given duration, size budget and resolution.
// Throws for a non-positive/non-finite duration or maxVideoSizeMB since dividing by such a
// value has no sensible answer.
export function computeBitrateBudget(
    durationSec: number,
    maxVideoSizeMB: number,
    resolution: NativeTargetResolution,
): BitrateBudget {
    if (!(durationSec > 0) || !isFinite(durationSec)) {
        throw new Error(`computeBitrateBudget: invalid durationSec=${durationSec}`)
    }
    if (!(maxVideoSizeMB > 0) || !isFinite(maxVideoSizeMB)) {
        throw new Error(`computeBitrateBudget: invalid maxVideoSizeMB=${maxVideoSizeMB}`)
    }

    const maxBytes = maxVideoSizeMB * 1024 * 1024
    const usableBytes = Math.floor(maxBytes * CONTAINER_SAFETY_MARGIN)
    const usableBits = usableBytes * 8

    const sizeDrivenBps = Math.floor(usableBits / durationSec)
    const ceilingBps = BITRATE_CEILING_BPS[resolution]

    const bitrateBps = Math.max(BITRATE_FLOOR_BPS, Math.min(sizeDrivenBps, ceilingBps))

    return { bitrateBps, usableBytes }
}

export function targetDimensions(resolution: NativeTargetResolution) {
    return NATIVE_RESOLUTIONS[resolution]
}

// ---- Finite retry state machine -------------------------------------------------------------
//
// Each attempt either succeeds (output within maxVideoSizeMB), or the caller advances to the
// next state via nextRetryState(). The sequence always terminates: it never revisits the same
// (resolution, bitrateStep) pair, and returns null once attempts are exhausted, at which point
// the caller falls back to the external compressor.

export interface NativeRetryState {
    resolution: NativeTargetResolution
    // How many times the bitrate has already been reduced below the size-driven budget.
    bitrateStep: number
}

// Bitrate is reduced by this fraction on each bounded retry step, after the 480p fallback has
// already been tried at its own size-driven bitrate.
const BITRATE_STEP_FACTOR = 0.7
const MAX_BITRATE_STEPS = 2

export function initialRetryState(preferredResolution: NativeTargetResolution): NativeRetryState {
    return { resolution: preferredResolution, bitrateStep: 0 }
}

// Returns the next state to attempt, or null when the native retry budget is exhausted (the
// caller should then fall back to the external compressor per the routing contract).
export function nextRetryState(state: NativeRetryState): NativeRetryState | null {
    if (state.resolution === 720) {
        return { resolution: 480, bitrateStep: 0 }
    }

    if (state.bitrateStep < MAX_BITRATE_STEPS) {
        return { resolution: 480, bitrateStep: state.bitrateStep + 1 }
    }

    return null
}

export function bitrateForRetryState(
    durationSec: number,
    maxVideoSizeMB: number,
    state: NativeRetryState,
): BitrateBudget {
    const budget = computeBitrateBudget(durationSec, maxVideoSizeMB, state.resolution)
    if (state.bitrateStep === 0) return budget

    const steppedBps = Math.max(
        BITRATE_FLOOR_BPS,
        Math.round(budget.bitrateBps * Math.pow(BITRATE_STEP_FACTOR, state.bitrateStep)),
    )
    return { bitrateBps: steppedBps, usableBytes: budget.usableBytes }
}

// True when the requested output size is mathematically unreachable even at the bitrate floor —
// i.e. encoding the whole duration at BITRATE_FLOOR_BPS would still exceed maxVideoSizeMB. In
// that case no native resolution/bitrate combination can ever satisfy maxVideoSizeMB, so the
// caller should skip native compression entirely rather than run one or more full transcodes
// that are guaranteed to come back oversized.
export function isNativeSizeBudgetFeasible(durationSec: number, maxVideoSizeMB: number): boolean {
    if (!(durationSec > 0) || !isFinite(durationSec)) {
        throw new Error(`isNativeSizeBudgetFeasible: invalid durationSec=${durationSec}`)
    }
    if (!(maxVideoSizeMB > 0) || !isFinite(maxVideoSizeMB)) {
        throw new Error(`isNativeSizeBudgetFeasible: invalid maxVideoSizeMB=${maxVideoSizeMB}`)
    }

    const usableBytes = Math.floor(maxVideoSizeMB * 1024 * 1024 * CONTAINER_SAFETY_MARGIN)
    const usableBits = usableBytes * 8
    const sizeDrivenBps = usableBits / durationSec

    return sizeDrivenBps >= BITRATE_FLOOR_BPS
}

// ---- Duplicate-free attempt sequencing ------------------------------------------------------
//
// bitrateForRetryState's floor-clamping means two consecutive bitrateStep values can resolve to
// the *same* effective bitrate once stepping bottoms out at BITRATE_FLOOR_BPS. Re-running an
// identical (resolution, bitrateBps) transcode produces no new information, so the attempt
// sequence below detects that and terminates instead of repeating it.

export interface NativeAttempt {
    state: NativeRetryState
    bitrateBps: number
}

export function firstNativeAttempt(
    durationSec: number,
    maxVideoSizeMB: number,
    preferredResolution: NativeTargetResolution,
): NativeAttempt {
    const state = initialRetryState(preferredResolution)
    const { bitrateBps } = bitrateForRetryState(durationSec, maxVideoSizeMB, state)
    return { state, bitrateBps }
}

// Returns the next attempt to run, or null when retries are exhausted OR the next candidate
// would be an exact (resolution, bitrateBps) repeat of `previous` — both are terminal: the
// caller should fall back to the external compressor.
export function nextNativeAttempt(
    durationSec: number,
    maxVideoSizeMB: number,
    previous: NativeAttempt,
): NativeAttempt | null {
    const nextState = nextRetryState(previous.state)
    if (nextState === null) return null

    const { bitrateBps } = bitrateForRetryState(durationSec, maxVideoSizeMB, nextState)
    if (nextState.resolution === previous.state.resolution && bitrateBps === previous.bitrateBps) {
        return null
    }

    return { state: nextState, bitrateBps }
}
